import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import type { PluginProblem } from '@dunea/shared';
import { apiDisableAppPlugin, apiEnableAppPlugin, apiGetAppPlugins, type AppPluginStatus } from '~/lib/api/plugins';
import { describeRequestError } from '~/lib/request-error';
import { classNames } from '~/utils/classNames';
import { generalProblems, pluginRows, type PluginRowView } from './plugins-model';

// The app's plugins: state, who turned them on, what's missing, and an on/off
// switch. Turning a plugin off keeps its data; turning one on only works when
// its code is already in the app (the server's 409 says to ask Dunea in chat).

const PROBLEM_STYLES: Record<PluginProblem['level'], { icon: string; color: string }> = {
  error: { icon: 'i-ph:warning-circle', color: 'text-[#F26A6A]' },
  warning: { icon: 'i-ph:warning', color: 'text-[#F2C46A]' },
  info: { icon: 'i-ph:info', color: 'text-muted' },
};

export function PluginsSection({ appId }: { appId: string }) {
  const [status, setStatus] = useState<AppPluginStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await apiGetAppPlugins(appId));
      setLoadError(null);
    } catch (err) {
      setLoadError(describeRequestError(err).serverMessage ?? 'Could not load this app’s plugins.');
    }
  }, [appId]);

  useEffect(() => {
    setStatus(null);
    void load();
  }, [load]);

  const toggle = async (row: PluginRowView) => {
    const turningOff = row.state === 'enabled';
    setBusy(row.id);
    setRowError(null);

    try {
      if (turningOff) {
        await apiDisableAppPlugin(appId, row.id);
      } else {
        await apiEnableAppPlugin(appId, row.id);
      }

      toast.success(`${row.title} turned ${turningOff ? 'off' : 'on'}.`);
      await load();
    } catch (err) {
      const message =
        describeRequestError(err).serverMessage ?? `Could not turn ${row.title} ${turningOff ? 'off' : 'on'}.`;
      setRowError({ id: row.id, message });
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <Section>
        <p className="text-xs text-muted">{loadError}</p>
      </Section>
    );
  }

  if (!status) {
    return (
      <Section>
        <div className="flex items-center justify-center py-4">
          <div className="i-svg-spinners:90-ring-with-bg text-base text-muted" />
        </div>
      </Section>
    );
  }

  const rows = pluginRows(status);
  const general = generalProblems(status, rows);

  return (
    <Section>
      {general.length > 0 && <ProblemList problems={general} />}

      {rows.length === 0 ? (
        <p className="text-xs text-muted bg-raised border border-hairline rounded-lg p-3">
          No plugins yet. Ask Dunea in chat to add a shop, payments, reviews or other features; they show up here.
        </p>
      ) : (
        rows.map((row) => (
          <PluginRow
            key={row.id}
            row={row}
            busy={busy === row.id}
            error={rowError?.id === row.id ? rowError.message : null}
            onToggle={() => toggle(row)}
          />
        ))
      )}
    </Section>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-muted uppercase tracking-wide">Plugins</h4>
      {children}
    </div>
  );
}

function PluginRow({
  row,
  busy,
  error,
  onToggle,
}: {
  row: PluginRowView;
  busy: boolean;
  error: string | null;
  onToggle: () => void;
}) {
  const on = row.state === 'enabled';

  return (
    <div className={classNames('bg-raised rounded-lg border border-hairline p-3', !on && 'opacity-70')}>
      <div className="flex items-start gap-3">
        <div className={classNames(row.icon, 'text-xl mt-0.5', on ? 'text-accent' : 'text-muted')} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-primary truncate">{row.title}</span>
              <span
                className={classNames(
                  'text-[10px] flex items-center gap-1 shrink-0',
                  on ? 'text-[#6EE7A6]' : 'text-muted',
                )}
              >
                <span className={on ? 'i-ph:check-circle' : 'i-ph:pause-circle'} />
                {on ? 'On' : 'Off'}
              </span>
            </div>
            {row.canToggle && (
              <button
                onClick={onToggle}
                disabled={busy}
                aria-label={`${on ? 'Turn off' : 'Turn on'} ${row.title}`}
                className={classNames(
                  'text-xs px-2.5 py-1 rounded-md transition-opacity disabled:opacity-50 shrink-0',
                  on
                    ? 'border border-hairline text-secondary hover:text-primary hover:bg-sunken'
                    : 'bg-accent text-black font-medium hover:opacity-90',
                )}
              >
                {busy ? '…' : on ? 'Turn off' : 'Turn on'}
              </button>
            )}
          </div>
          <p className="text-xs text-muted mt-0.5">{row.description}</p>
          <p className="text-[10px] text-muted mt-1">{row.sourceLabel}</p>
          {row.problems.length > 0 && (
            <div className="mt-2">
              <ProblemList problems={row.problems} />
            </div>
          )}
          {error && (
            <p className="text-xs text-[#F26A6A] mt-2" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ProblemList({ problems }: { problems: readonly PluginProblem[] }) {
  return (
    <ul className="space-y-1">
      {problems.map((p, i) => {
        const style = PROBLEM_STYLES[p.level];

        return (
          <li key={`${p.level}-${i}`} className={classNames('text-xs flex items-start gap-1.5', style.color)}>
            <span className={classNames(style.icon, 'mt-0.5 shrink-0')} />
            <span>{p.message}</span>
          </li>
        );
      })}
    </ul>
  );
}
